import {
  Calendar,
  Clock,
  Users,
  FileText,
  type LucideIcon,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { TruncatedText } from "@/components/ui";
import { cn } from "@/lib/utils";
import {
  ActivityAvailabilityMode,
  BookingQuantityMode,
  type HandshakeContext,
} from "@/types";
import { formatActivityDateTime } from "@/components/bookings/formatActivityDateTime";

interface HandshakeBookingDetailsProps {
  context: HandshakeContext;
  /** Full-width stat pills under the chat header */
  variant?: "strip" | "inline";
  className?: string;
}

function quantityLabelKey(
  mode: BookingQuantityMode,
): "partyGuests" | "partyQuantity" {
  return mode === BookingQuantityMode.GUESTS ? "partyGuests" : "partyQuantity";
}

function visitNotSetShortKey(
  availabilityMode: ActivityAvailabilityMode,
): "visitOpenShort" | "visitScheduledMissingShort" {
  return availabilityMode === ActivityAvailabilityMode.SCHEDULED
    ? "visitScheduledMissingShort"
    : "visitOpenShort";
}

function DetailPill({
  icon: Icon,
  children,
  className,
  title,
}: {
  icon: LucideIcon;
  children: string;
  className?: string;
  title?: string;
}) {
  return (
    <span
      title={title}
      className={cn(
        "inline-flex max-w-full items-center gap-1.5 rounded-lg border border-surface-200/90 bg-surface-0 px-2.5 py-1.5 text-sm text-surface-800 shadow-sm",
        className
      )}
    >
      <Icon className="h-3.5 w-3.5 shrink-0 text-surface-400" aria-hidden />
      <TruncatedText className="font-medium">{children}</TruncatedText>
    </span>
  );
}

export function HandshakeBookingDetails({
  context,
  variant = "strip",
  className,
}: HandshakeBookingDetailsProps) {
  const { t } = useTranslation("handshakePages");

  const quantity = context.quantity;
  const visitAt = context.visit_at;
  const timeSlot = context.time_slot;
  const notes = context.customer_notes?.trim() || null;
  const visitNotSetKey = visitNotSetShortKey(context.availability_mode);

  const partyLabel =
    quantity > 0
      ? t(`bookingDetails.${quantityLabelKey(context.booking_quantity_mode)}`, {
          count: quantity,
        })
      : null;

  const regionCode = context.activity_region_code;
  const visitLabels = visitAt
    ? formatActivityDateTime(visitAt, regionCode, () => "")
    : null;
  const visitDateLabel = visitLabels?.dateLabel ?? null;
  const visitTimeLabel =
    timeSlot?.trim() || visitLabels?.timeLabel || null;
  const visitLabel =
    visitDateLabel && visitTimeLabel
      ? `${visitDateLabel} · ${visitTimeLabel}`
      : visitDateLabel ?? visitTimeLabel;

  if (variant === "inline") {
    return (
      <div
        className={cn("mt-2 flex flex-wrap items-center gap-x-2 gap-y-1.5 text-sm", className)}
      >
        {partyLabel ? (
          <span className="inline-flex items-center gap-1 font-medium text-surface-700">
            <Users className="h-3.5 w-3.5 shrink-0 text-surface-400" />
            {partyLabel}
          </span>
        ) : null}
        {visitLabel ? (
          <span className="inline-flex items-center gap-1 text-surface-700">
            <Calendar className="h-3.5 w-3.5 shrink-0 text-surface-400" />
            {visitLabel}
          </span>
        ) : (
          <span className="inline-flex items-center gap-1 text-warning-700">
            <Clock className="h-3.5 w-3.5 shrink-0" />
            {t(`bookingDetails.${visitNotSetKey}`)}
          </span>
        )}
      </div>
    );
  }

  return (
    <div className={cn("flex flex-wrap gap-2", className)}>
      {partyLabel ? <DetailPill icon={Users}>{partyLabel}</DetailPill> : null}
      {visitLabel ? (
        <DetailPill icon={Calendar} title={visitLabel}>
          {visitLabel}
        </DetailPill>
      ) : (
        <DetailPill
          icon={Clock}
          className="border-warning-200/90 bg-warning-50/60 text-warning-900"
          title={t(`bookingDetails.${visitNotSetKey}`)}
        >
          {t(`bookingDetails.${visitNotSetKey}`)}
        </DetailPill>
      )}
      {notes ? (
        <DetailPill icon={FileText} title={notes} className="max-w-full sm:max-w-md">
          {notes}
        </DetailPill>
      ) : null}
    </div>
  );
}
